import {useState} from "react";
import {css} from "@panda/css";
import {css as pandaCss} from "@panda/css";
import {AnimatePresence, motion} from "framer-motion";

import {useUserDevice} from "@pg-mono/user-device";

import {rpFeatureFlagMultiLeadInsideConversations} from "../../../../../app/read_rp_environment_variables";
import {IApplication} from "../../../../../application/types/IApplication";
import {IMultiLeadStepPayload} from "../../../../../application/types/IMultiLeadStepPayload";
import {ISetPreferencesStepPayload} from "../../../../../application/types/ISetPreferencesStepPayload";
import {CloseIconButton} from "../../../../../atoms/CloseIconButton";
import {useShouldShowPriceByOfferConfigAndUser} from "../../../../../hooks/use_should_show_price_by_offer_config_and_user";
import {useGetOfferDetailApplicationQuery} from "../../../../../offer/api/get_offer_detail";
import {getOfferTypePluralize} from "../../../../../offer/helpers/OfferType";
import {IRecommendedOffer} from "../../../../../offer/types/IRecommendedOffer";
import {OfferType} from "../../../../../offer/types/OfferType";
import {unlockPrices} from "../../../../../prices/state/prices_unlocked_slice";
import {IRecommendedProperty} from "../../../../../property/types/IRecommendedProperty";
import {useAppDispatch} from "../../../../../store/hooks";
import {useGetVendorDetailApplicationQuery} from "../../../../../vendor/api/get_vendor_detail_application_api";
import {PriceVisibilityType} from "../../../../../vendor/types/PriceVisibilityType";
import {askForPriceleadButtonCopy} from "../../../../constants/lead_button_copy";
import {useLeadRequiredParams} from "../../../../hooks/use_lead_required_params";
import {ILeadModalPriceConversationPrice} from "../../../../types/ILeadModalPriceConversationPrice";
import {LeadRodoTerms} from "../../../LeadRodoTerms";
import {LeadVendorRodoTerms} from "../../../LeadVendorRodoTerms";
import {LeadModalPriceConversationChatVariantBCD} from "./lead_modal_price_conversation_chat/LeadModalPriceConversationChatVariantBCD";
import {LeadModalPriceConversationConsultant} from "./LeadModalPriceConversationConsultant";
import {LeadModalPriceConversationMultiLeadActions} from "./LeadModalPriceConversationMultiLeadActions";
import {LeadModalPriceConversationMultiLeadDetails} from "./LeadModalPriceConversationMultiLeadDetails";
import {LeadModalPriceConversationPropertyInfo} from "./LeadModalPriceConversationPropertyInfo";

interface IProps {
    handleModalClose: () => void;
    handleUpdateMainLeadResponse: (sentApplication: IApplication) => void;
    handleSetPreferencesStep: (payload: ISetPreferencesStepPayload) => void;
    price: number | null;
    storeHash: string;
}

export function LeadModalPriceConversation(props: IProps) {
    const {handleModalClose, handleSetPreferencesStep} = props;

    const dispatch = useAppDispatch();
    const {isMobile} = useUserDevice();
    const {vendorId, offerId} = useLeadRequiredParams();

    const {data: vendorDetail} = useGetVendorDetailApplicationQuery(vendorId, {skip: !vendorId});
    const {data: offerDetail} = useGetOfferDetailApplicationQuery(offerId as number, {skip: !offerId});

    const shouldShowPrice = useShouldShowPriceByOfferConfigAndUser({
        offerPriceVisibility: PriceVisibilityType.ALWAYS,
        vendorPriceVisibility: PriceVisibilityType.AUTO,
        vendorId
    });

    const [hasLeadBeenSent, setHasLeadBeenSent] = useState(false);
    const [mutiLeadDetails, setMultiLeadDetails] = useState<IMultiLeadStepPayload | null>(null);
    const shouldShowMultiLeadDetails = rpFeatureFlagMultiLeadInsideConversations && !!mutiLeadDetails;
    const [price, setPrice] = useState<null | ILeadModalPriceConversationPrice>(() => {
        if (props.price && shouldShowPrice) {
            return {
                min: props.price,
                max: props.price
            };
        }

        return null;
    });

    const handleSetMultiLeadStep = (payload: IMultiLeadStepPayload) => {
        if (rpFeatureFlagMultiLeadInsideConversations) {
            setMultiLeadDetails(payload);
        }
    };

    const handleUpdateMainLeadResponse = (sentApplication: IApplication) => {
        setHasLeadBeenSent(true);
        props.handleUpdateMainLeadResponse(sentApplication);
    };

    const leadButtonText = props.price ? "Prześlij zapytanie" : askForPriceleadButtonCopy;

    const handleRevealPrice = (priceRange: ILeadModalPriceConversationPrice) => {
        if (props.price) {
            setPrice({
                min: props.price,
                max: props.price
            });
        } else {
            setPrice(priceRange);
        }

        dispatch(unlockPrices());
    };

    return (
        <div className={conversationStyles}>
            <CloseIconButton className={closeButtonStyles} onClick={handleModalClose} size="1.6" top={2} right={2} />
            {isMobile && <LeadModalPriceConversationConsultant className={stickyConsultantStyles} variant="small" />}

            <div className={firstColumnStyles}>
                <AnimatePresence>
                    {(!isMobile || !hasLeadBeenSent) && (
                        <motion.div
                            key="lmpc-property-info"
                            initial={{height: "auto"}}
                            animate={{height: "auto"}}
                            exit={{
                                height: 0,
                                marginTop: 0,
                                marginBottom: 0,
                                paddingTop: 0,
                                paddingBottom: 0
                            }}
                            transition={{
                                duration: 0.3,
                                ease: "easeOut"
                            }}
                            style={isMobile ? {overflow: "hidden"} : undefined}
                        >
                            <LeadModalPriceConversationPropertyInfo
                                variant={shouldShowMultiLeadDetails || isMobile ? "small" : "large"}
                                estimatedPrice={price}
                                className={propertyInfoWrapperStyles}
                            />
                        </motion.div>
                    )}
                </AnimatePresence>

                {!isMobile && shouldShowMultiLeadDetails && rpFeatureFlagMultiLeadInsideConversations && (
                    <LeadModalPriceConversationMultiLeadDetails recommendations={mutiLeadDetails.recommendedOffers} className={multiLeadLeftColumnStyles} />
                )}

                {!isMobile && !shouldShowMultiLeadDetails && (
                    <div className={desktopLeadTerms}>
                        <LeadRodoTerms />
                        {vendorDetail && (
                            <LeadVendorRodoTerms
                                vendor={vendorDetail}
                                buttonText={leadButtonText}
                                offerPrivacyPolicyUrl={offerDetail?.configuration.privacy_policy_url}
                                offerPersonalDataProcessorName={offerDetail?.configuration.personal_data_processor_name}
                                offerPersonalDataProcessorUrl={offerDetail?.configuration.personal_data_processor_url}
                            />
                        )}
                    </div>
                )}
            </div>

            <LeadModalPriceConversationChatVariantBCD
                headerSlot={!isMobile && <LeadModalPriceConversationConsultant />}
                handleModalClose={handleModalClose}
                handleSetPreferencesStep={handleSetPreferencesStep}
                handleSetPrice={handleRevealPrice}
                handleSetMultiLeadStep={handleSetMultiLeadStep}
                handleUpdateMainLeadResponse={handleUpdateMainLeadResponse}
                showPropertyInfoAfterLead={!!isMobile}
                price={price}
                hideRodoTerms={shouldShowMultiLeadDetails}
                className={secondColumnStyles}
            >
                {shouldShowMultiLeadDetails && rpFeatureFlagMultiLeadInsideConversations
                    ? ({ConversationItem}) => (
                          <ConversationItem
                              initialVisibleMessagesCount={1}
                              messages={[
                                  {
                                      messageContent: (
                                          <>
                                              Znaleźliśmy jeszcze{" "}
                                              <strong>
                                                  {getRecommendationTypePluralize(mutiLeadDetails.recommendedOffers)} o podobnych parametrach i układzie
                                                  funkcjonalnym
                                              </strong>{" "}
                                              w najbliżej okolicy.
                                          </>
                                      ),
                                      className: multileadMessageStyles
                                  }
                              ]}
                              actions={
                                  <>
                                      {isMobile ? (
                                          <LeadModalPriceConversationMultiLeadDetails
                                              disableScroll
                                              disableHeader
                                              recommendations={mutiLeadDetails.recommendedOffers}
                                          />
                                      ) : undefined}

                                      <LeadModalPriceConversationMultiLeadActions
                                          recommendations={mutiLeadDetails.recommendedOffers}
                                          handleSetPreferencesState={handleSetPreferencesStep}
                                          storeHash={props.storeHash}
                                      />
                                  </>
                              }
                          />
                      )
                    : null}
            </LeadModalPriceConversationChatVariantBCD>
        </div>
    );
}

const getOfferTypeFromRecommendation = (recommendation?: IRecommendedOffer | IRecommendedProperty): OfferType => {
    if (recommendation && "offer" in recommendation) {
        return recommendation.offer.type || OfferType.FLAT;
    }

    return recommendation?.type || OfferType.FLAT;
};

const getRecommendationTypePluralize = (recommendation?: IRecommendedOffer[] | IRecommendedProperty[] | null) => {
    if (!recommendation || recommendation.length <= 1) {
        return "";
    }

    const offerType = getOfferTypeFromRecommendation(recommendation[0]);
    return `${recommendation.length} ${getOfferTypePluralize(recommendation.length, offerType)}`;
};

// Styles
const conversationStyles = css({
    display: "flex",
    flexDirection: "column",
    position: "relative",
    height: "100%",
    width: "100%",

    md: {
        flexDirection: "row",
        gap: 2
    }
});

const firstColumnStyles = css({
    md: {
        display: "flex",
        flexDirection: "column",
        height: "100%",
        flex: "1 1 50%",
        py: 3,
        pl: 3
    }
});

const secondColumnStyles = css({
    md: {
        flex: "1 1 50%",
        py: 3,
        pr: 3
    }
});

const closeButtonStyles = css({
    position: "fixed",
    right: 1.5,
    top: 1.5,
    borderRadius: "50%",
    boxShadow: "var(--shadow-2)",
    background: "white",
    width: "3.2rem",
    height: "3.2rem",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 11,
    md: {
        position: "absolute",
        top: 2,
        right: 2
    }
});

const propertyInfoWrapperStyles = css({
    position: "relative",
    zIndex: 2,
    backgroundColor: "#fff",
    py: 2,
    md: {
        py: 0
    }
});

const multiLeadLeftColumnStyles = css({
    md: {
        mt: 4,
        flex: "1 1 auto",
        minHeight: 0
    }
});

const multileadMessageStyles = css({
    mr: 4,
    overflow: "visible"
});

const stickyConsultantStyles = css({
    position: "sticky",
    top: 0,
    zIndex: 10,
    backgroundColor: "#fff",
    borderBottom: "1px solid var(--colors-gray-500)"
});

const desktopLeadTerms = pandaCss({
    display: "flex",
    flexDirection: "column",
    gap: 2,
    py: 2,
    flex: "0 0 auto",
    textAlign: "center"
});
import {useState} from "react";
import {css} from "@panda/css";
import {css as pandaCss} from "@panda/css";
import {AnimatePresence, motion} from "framer-motion";

import {useUserDevice} from "@pg-mono/user-device";

import {rpFeatureFlagMultiLeadInsideConversations} from "../../../../../app/read_rp_environment_variables";
import {IApplication} from "../../../../../application/types/IApplication";
import {IMultiLeadStepPayload} from "../../../../../application/types/IMultiLeadStepPayload";
import {ISetPreferencesStepPayload} from "../../../../../application/types/ISetPreferencesStepPayload";
import {CloseIconButton} from "../../../../../atoms/CloseIconButton";
import {useShouldShowPriceByOfferConfigAndUser} from "../../../../../hooks/use_should_show_price_by_offer_config_and_user";
import {useGetOfferDetailApplicationQuery} from "../../../../../offer/api/get_offer_detail";
import {getOfferTypePluralize} from "../../../../../offer/helpers/OfferType";
import {IRecommendedOffer} from "../../../../../offer/types/IRecommendedOffer";
import {OfferType} from "../../../../../offer/types/OfferType";
import {unlockPrices} from "../../../../../prices/state/prices_unlocked_slice";
import {IRecommendedProperty} from "../../../../../property/types/IRecommendedProperty";
import {useAppDispatch} from "../../../../../store/hooks";
import {useGetVendorDetailApplicationQuery} from "../../../../../vendor/api/get_vendor_detail_application_api";
import {PriceVisibilityType} from "../../../../../vendor/types/PriceVisibilityType";
import {askForPriceleadButtonCopy} from "../../../../constants/lead_button_copy";
import {useLeadRequiredParams} from "../../../../hooks/use_lead_required_params";
import {ILeadModalPriceConversationPrice} from "../../../../types/ILeadModalPriceConversationPrice";
import {LeadRodoTerms} from "../../../LeadRodoTerms";
import {LeadVendorRodoTerms} from "../../../LeadVendorRodoTerms";
import {LeadModalPriceConversationChatVariantBCD} from "./lead_modal_price_conversation_chat/LeadModalPriceConversationChatVariantBCD";
import {LeadModalPriceConversationConsultant} from "./LeadModalPriceConversationConsultant";
import {LeadModalPriceConversationMultiLeadActions} from "./LeadModalPriceConversationMultiLeadActions";
import {LeadModalPriceConversationMultiLeadDetails} from "./LeadModalPriceConversationMultiLeadDetails";
import {LeadModalPriceConversationPropertyInfo} from "./LeadModalPriceConversationPropertyInfo";

interface IProps {
    handleModalClose: () => void;
    handleUpdateMainLeadResponse: (sentApplication: IApplication) => void;
    handleSetPreferencesStep: (payload: ISetPreferencesStepPayload) => void;
    price: number | null;
    storeHash: string;
}

export function LeadModalPriceConversation(props: IProps) {
    const {handleModalClose, handleSetPreferencesStep} = props;

    const dispatch = useAppDispatch();
    const {isMobile} = useUserDevice();
    const {vendorId, offerId} = useLeadRequiredParams();

    const {data: vendorDetail} = useGetVendorDetailApplicationQuery(vendorId, {skip: !vendorId});
    const {data: offerDetail} = useGetOfferDetailApplicationQuery(offerId as number, {skip: !offerId});

    const shouldShowPrice = useShouldShowPriceByOfferConfigAndUser({
        offerPriceVisibility: PriceVisibilityType.ALWAYS,
        vendorPriceVisibility: PriceVisibilityType.AUTO,
        vendorId
    });

    const [hasLeadBeenSent, setHasLeadBeenSent] = useState(false);
    const [mutiLeadDetails, setMultiLeadDetails] = useState<IMultiLeadStepPayload | null>(null);
    const shouldShowMultiLeadDetails = rpFeatureFlagMultiLeadInsideConversations && !!mutiLeadDetails;
    const [price, setPrice] = useState<null | ILeadModalPriceConversationPrice>(() => {
        if (props.price && shouldShowPrice) {
            return {
                min: props.price,
                max: props.price
            };
        }

        return null;
    });

    const handleSetMultiLeadStep = (payload: IMultiLeadStepPayload) => {
        if (rpFeatureFlagMultiLeadInsideConversations) {
            setMultiLeadDetails(payload);
        }
    };

    const handleUpdateMainLeadResponse = (sentApplication: IApplication) => {
        setHasLeadBeenSent(true);
        props.handleUpdateMainLeadResponse(sentApplication);
    };

    const leadButtonText = props.price ? "Prześlij zapytanie" : askForPriceleadButtonCopy;

    const handleRevealPrice = (priceRange: ILeadModalPriceConversationPrice) => {
        if (props.price) {
            setPrice({
                min: props.price,
                max: props.price
            });
        } else {
            setPrice(priceRange);
        }

        dispatch(unlockPrices());
    };

    return (
        <div className={conversationStyles}>
            <CloseIconButton className={closeButtonStyles} onClick={handleModalClose} size="1.6" top={2} right={2} />
            {isMobile && <LeadModalPriceConversationConsultant className={stickyConsultantStyles} variant="small" />}

            <div className={firstColumnStyles}>
                <AnimatePresence>
                    {(!isMobile || !hasLeadBeenSent) && (
                        <motion.div
                            key="lmpc-property-info"
                            initial={{height: "auto"}}
                            animate={{height: "auto"}}
                            exit={{
                                height: 0,
                                marginTop: 0,
                                marginBottom: 0,
                                paddingTop: 0,
                                paddingBottom: 0
                            }}
                            transition={{
                                duration: 0.3,
                                ease: "easeOut"
                            }}
                            style={isMobile ? {overflow: "hidden"} : undefined}
                        >
                            <LeadModalPriceConversationPropertyInfo
                                variant={shouldShowMultiLeadDetails || isMobile ? "small" : "large"}
                                estimatedPrice={price}
                                className={propertyInfoWrapperStyles}
                            />
                        </motion.div>
                    )}
                </AnimatePresence>

                {!isMobile && shouldShowMultiLeadDetails && rpFeatureFlagMultiLeadInsideConversations && (
                    <LeadModalPriceConversationMultiLeadDetails recommendations={mutiLeadDetails.recommendedOffers} className={multiLeadLeftColumnStyles} />
                )}

                {!isMobile && !shouldShowMultiLeadDetails && (
                    <div className={desktopLeadTerms}>
                        <LeadRodoTerms />
                        {vendorDetail && (
                            <LeadVendorRodoTerms
                                vendor={vendorDetail}
                                buttonText={leadButtonText}
                                offerPrivacyPolicyUrl={offerDetail?.configuration.privacy_policy_url}
                                offerPersonalDataProcessorName={offerDetail?.configuration.personal_data_processor_name}
                                offerPersonalDataProcessorUrl={offerDetail?.configuration.personal_data_processor_url}
                            />
                        )}
                    </div>
                )}
            </div>

            <LeadModalPriceConversationChatVariantBCD
                headerSlot={!isMobile && <LeadModalPriceConversationConsultant />}
                handleModalClose={handleModalClose}
                handleSetPreferencesStep={handleSetPreferencesStep}
                handleSetPrice={handleRevealPrice}
                handleSetMultiLeadStep={handleSetMultiLeadStep}
                handleUpdateMainLeadResponse={handleUpdateMainLeadResponse}
                showPropertyInfoAfterLead={!!isMobile}
                price={price}
                hideRodoTerms={shouldShowMultiLeadDetails}
                className={secondColumnStyles}
            >
                {shouldShowMultiLeadDetails && rpFeatureFlagMultiLeadInsideConversations
                    ? ({ConversationItem}) => (
                          <ConversationItem
                              initialVisibleMessagesCount={1}
                              messages={[
                                  {
                                      messageContent: (
                                          <>
                                              Znaleźliśmy jeszcze{" "}
                                              <strong>
                                                  {getRecommendationTypePluralize(mutiLeadDetails.recommendedOffers)} o podobnych parametrach i układzie
                                                  funkcjonalnym
                                              </strong>{" "}
                                              w najbliżej okolicy.
                                          </>
                                      ),
                                      className: multileadMessageStyles
                                  }
                              ]}
                              actions={
                                  <>
                                      {isMobile ? (
                                          <LeadModalPriceConversationMultiLeadDetails
                                              disableScroll
                                              disableHeader
                                              recommendations={mutiLeadDetails.recommendedOffers}
                                          />
                                      ) : undefined}

                                      <LeadModalPriceConversationMultiLeadActions
                                          recommendations={mutiLeadDetails.recommendedOffers}
                                          handleSetPreferencesState={handleSetPreferencesStep}
                                          storeHash={props.storeHash}
                                      />
                                  </>
                              }
                          />
                      )
                    : null}
            </LeadModalPriceConversationChatVariantBCD>
        </div>
    );
}

const getOfferTypeFromRecommendation = (recommendation?: IRecommendedOffer | IRecommendedProperty): OfferType => {
    if (recommendation && "offer" in recommendation) {
        return recommendation.offer.type || OfferType.FLAT;
    }

    return recommendation?.type || OfferType.FLAT;
};

const getRecommendationTypePluralize = (recommendation?: IRecommendedOffer[] | IRecommendedProperty[] | null) => {
    if (!recommendation || recommendation.length <= 1) {
        return "";
    }

    const offerType = getOfferTypeFromRecommendation(recommendation[0]);
    return `${recommendation.length} ${getOfferTypePluralize(recommendation.length, offerType)}`;
};

// Styles
const conversationStyles = css({
    display: "flex",
    flexDirection: "column",
    position: "relative",
    height: "100%",
    width: "100%",

    md: {
        flexDirection: "row",
        gap: 2
    }
});

const firstColumnStyles = css({
    md: {
        display: "flex",
        flexDirection: "column",
        height: "100%",
        flex: "1 1 50%",
        py: 3,
        pl: 3
    }
});

const secondColumnStyles = css({
    md: {
        flex: "1 1 50%",
        py: 3,
        pr: 3
    }
});

const closeButtonStyles = css({
    position: "fixed",
    right: 1.5,
    top: 1.5,
    borderRadius: "50%",
    boxShadow: "var(--shadow-2)",
    background: "white",
    width: "3.2rem",
    height: "3.2rem",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 11,
    md: {
        position: "absolute",
        top: 2,
        right: 2
    }
});

const propertyInfoWrapperStyles = css({
    position: "relative",
    zIndex: 2,
    backgroundColor: "#fff",
    py: 2,
    md: {
        py: 0
    }
});

const multiLeadLeftColumnStyles = css({
    md: {
        mt: 4,
        flex: "1 1 auto",
        minHeight: 0
    }
});

const multileadMessageStyles = css({
    mr: 4,
    overflow: "visible"
});

const stickyConsultantStyles = css({
    position: "sticky",
    top: 0,
    zIndex: 10,
    backgroundColor: "#fff",
    borderBottom: "1px solid var(--colors-gray-500)"
});

const desktopLeadTerms = pandaCss({
    display: "flex",
    flexDirection: "column",
    gap: 2,
    py: 2,
    flex: "0 0 auto",
    textAlign: "center"
});
